import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SafeTurnstile } from "@/components/SafeTurnstile";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const MINT = "#00E5C0";

const input =
  "w-full bg-[#050505] px-4 py-3 text-sm text-white outline-none ring-1 ring-zinc-800 transition-shadow focus:ring-[#00E5C0]";
const label = "mb-2 block text-[10px] uppercase tracking-[0.25em] text-zinc-500";

export default function Waitlist() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [email, setEmail] = useState("");
  /** Multi-select interest ids (coming-soon Shundos only — never hunting_fleet). */
  const [selectedIds, setSelectedIds] = useState([]);
  const [fleetMapped, setFleetMapped] = useState(false);
  const [token, setToken] = useState("");
  const [captchaBroken, setCaptchaBroken] = useState(false);
  const [busy, setBusy] = useState(false);
  const turnstileRef = useRef(null);

  const queryProduct = searchParams.get("product") || "";
  const focusFleet = searchParams.get("focus") === "hunting_fleet";

  useEffect(() => {
    api
      .get("/products")
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : [];
        const soon = [];
        const seen = new Set();
        for (const p of list) {
          if (!p?.coming_soon || !p?.id || seen.has(p.id)) continue;
          seen.add(p.id);
          soon.push(p);
        }
        setProducts(soon);
      })
      .catch(() => setProducts([]))
      .finally(() => setProductsLoaded(true));
  }, []);

  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user]);

  const interestOptions = useMemo(() => {
    const opts = products.map((p) => ({ id: p.id, name: p.name }));
    // Ensure query product appears even if not in coming_soon list
    if (queryProduct && !opts.some((o) => o.id === queryProduct)) {
      opts.push({ id: queryProduct, name: "Selected product" });
    }
    return opts;
  }, [products, queryProduct]);

  // Deep-link / focus mapping — never invent a hunting_fleet chip.
  useEffect(() => {
    if (queryProduct) {
      setSelectedIds((prev) =>
        prev.includes(queryProduct) ? prev : [...prev, queryProduct]
      );
      return;
    }
    if (!focusFleet || fleetMapped || !productsLoaded) return;
    if (interestOptions.length > 0) {
      // Select both (all) coming-soon Shundos — no "fleet all" chip.
      setSelectedIds(interestOptions.map((o) => o.id));
    }
    // If none available: leave empty → prompt UI + submit toast.
    setFleetMapped(true);
  }, [queryProduct, focusFleet, interestOptions, fleetMapped, productsLoaded]);

  const toggleInterest = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const resetCaptcha = () => {
    setToken("");
    try {
      turnstileRef.current?.reset?.();
    } catch (_) {
      /* widget may not be mounted */
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = (email || "").trim();
    if (!trimmed) {
      toast.error("Enter an email for waitlist access");
      return;
    }
    if (selectedIds.length === 0) {
      toast.error("Pick at least one interest", {
        description: focusFleet
          ? "Choose one or both Shundo hunts to join the fleet waitlist."
          : "Select a coming-soon product above.",
      });
      return;
    }
    if (!token && !captchaBroken && SITE_KEY) {
      toast.error("Complete the verification first");
      return;
    }
    setBusy(true);
    try {
      // One captcha verify; backend upserts every selected Shundo id.
      const primary = selectedIds[0];
      await api.post("/waitlist", {
        email: trimmed,
        product_id: primary,
        product_ids: selectedIds,
        turnstile_token: token || "captcha-unavailable",
      });
      toast.success("You're on the waitlist", {
        description: `We'll email ${trimmed} when these products open.`,
      });
      setToken("");
      resetCaptcha();
    } catch (err) {
      toast.error(apiError(err));
      resetCaptcha();
    } finally {
      setBusy(false);
    }
  };

  const showFleetPrompt =
    focusFleet && productsLoaded && selectedIds.length === 0;

  return (
    <div data-testid="waitlist-page" className="mx-auto max-w-md px-5 py-20 lg:py-28">
      <p
        className="text-xs font-bold uppercase tracking-[0.35em]"
        style={{ color: MINT }}
      >
        // waitlist
      </p>
      <h1 className="mt-4 font-display text-3xl tracking-tighter">Join the waitlist</h1>
      <p className="mt-3 text-xs leading-relaxed text-zinc-500">
        You&apos;ll be notified when the products you&apos;re interested in become available.
      </p>
      {showFleetPrompt && (
        <p
          data-testid="waitlist-fleet-prompt"
          className="mt-4 border border-[#00E5C0]/30 bg-[#00E5C0]/5 px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-[#00E5C0]"
        >
          Hunting Fleet — select one or both Shundo hunts below.
        </p>
      )}

      <form
        onSubmit={submit}
        data-testid="waitlist-form"
        className="mt-10 space-y-6 border border-[#1f1f1f] bg-[#0a0a0a] p-6"
      >
        <div>
          <label className={label} htmlFor="waitlist-email">
            Email
          </label>
          <input
            id="waitlist-email"
            data-testid="waitlist-email"
            className={input}
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            readOnly={!!user?.email}
            placeholder="you@email.com"
          />
        </div>

        {interestOptions.length > 0 && (
          <fieldset data-testid="waitlist-interest">
            <legend className={label}>Interest</legend>
            <div className="mt-1 flex flex-wrap gap-2">
              {interestOptions.map((opt) => {
                const active = selectedIds.includes(opt.id);
                return (
                  <button
                    key={opt.id}
                    type="button"
                    data-testid={`waitlist-chip-${opt.id}`}
                    aria-pressed={active}
                    onClick={() => toggleInterest(opt.id)}
                    className={`border px-3 py-2 text-[10px] uppercase tracking-[0.2em] transition-colors ${
                      active
                        ? "border-[#00E5C0] bg-[#00E5C0]/10 text-[#00E5C0]"
                        : "border-zinc-700 text-zinc-400 hover:border-[#00E5C0] hover:text-[#00E5C0]"
                    }`}
                  >
                    {opt.name}
                  </button>
                );
              })}
            </div>
          </fieldset>
        )}

        {SITE_KEY ? (
          <div className="min-h-[65px]" data-testid="waitlist-turnstile">
            <SafeTurnstile
              ref={turnstileRef}
              id="cf-turnstile-waitlist-page"
              siteKey={SITE_KEY}
              onSuccess={(t) => {
                setToken(t);
                setCaptchaBroken(false);
              }}
              onExpire={resetCaptcha}
              onUnsupported={() => {
                setCaptchaBroken(true);
                setToken("");
              }}
              onUnavailable={() => {
                setCaptchaBroken(true);
                setToken("");
              }}
              options={{ theme: "dark", size: "normal", action: "waitlist" }}
            />
            {captchaBroken && (
              <p className="mt-2 text-[10px] leading-relaxed text-zinc-500">
                Verification could not load on this domain — the server still checks the token.
              </p>
            )}
          </div>
        ) : (
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#f4d03f]">
            CAPTCHA is not configured.
          </p>
        )}

        <button
          type="submit"
          data-testid="waitlist-submit"
          disabled={busy || !SITE_KEY}
          className="w-full border border-[#00E5C0] py-3 text-[10px] uppercase tracking-[0.25em] text-[#00E5C0] transition-colors hover:bg-[#00E5C0] hover:text-black disabled:opacity-40"
        >
          {busy ? "Joining…" : "Join Waitlist"}
        </button>
      </form>

      <p className="mt-8 text-center text-[10px] uppercase tracking-[0.25em] text-zinc-600">
        <Link to="/" className="text-[#00E5C0] hover:underline">
          Back to vault
        </Link>
      </p>
    </div>
  );
}
